/**
 * Persisted light/dark preference for product shells (creator/auth/admin/legal).
 * Editorial docs/content still force light via useShellTheme / layout theme init.
 */
import { computed, ref, type Ref } from 'vue';

const STORAGE_KEY = 'gabber-color-mode';

export type ThemePreference = 'light' | 'dark';

/**
 * cas-0573: THE fallback when the person has not chosen a theme, used by every
 * user-facing shell, including standalone pages. An explicit saved choice wins;
 * the OS color scheme does not change the product default (Ben, 2026-10-07).
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function readStoredPreference(): ThemePreference {
  if (!canUseStorage()) return DEFAULT_THEME_PREFERENCE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    // ignore storage failures (private mode, etc.)
  }
  return DEFAULT_THEME_PREFERENCE;
}

function writeStoredPreference(value: ThemePreference) {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore storage failures
  }
}

function preferenceState(): Ref<ThemePreference> {
  // Prefer Nuxt useState when available so layout + nav share one value.
  const nuxtUseState = (
    globalThis as {
      useState?: <T>(key: string, init: () => T) => Ref<T>;
    }
  ).useState;
  if (typeof nuxtUseState === 'function') {
    return nuxtUseState('gabber-theme-preference', () => readStoredPreference());
  }
  return ref(readStoredPreference());
}

export function useThemePreference() {
  const preference = preferenceState();

  const isDark = computed(() => preference.value === 'dark');

  const setPreference = (value: ThemePreference) => {
    preference.value = value;
    writeStoredPreference(value);
  };

  const setDark = (dark: boolean) => {
    setPreference(dark ? 'dark' : 'light');
  };

  const toggle = () => {
    setDark(!isDark.value);
  };

  return {
    preference,
    isDark,
    setPreference,
    setDark,
    toggle,
    readStoredPreference
  };
}
